import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Grid, Modal, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { AxiosError } from "axios";

import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import ContactsList from "../components/ContactsList";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import EditContactModal from "../AddContactModal";
import Button, { Color } from "../../../../../../../../components/Form/Button";

import { keysToCamel } from "../../../../../../../../util/convertCamelCase";

import { Contact } from "../../../../../../../../models/contact";
import ContactService from "../../../../../../../../services/contact.service";

import useStyles from "./styles";

interface Props {
  contacts?: Contact[];
  handleCloseModal: () => void;
  refetchCompanyContacts: () => void;
  permission?: {
    edit: boolean;
  };
}

const ContactsListModal = ({
  contacts,
  handleCloseModal,
  refetchCompanyContacts,
  permission,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [contact, setContact] = useState<Contact>();
  const [openAddContactModal, setOpenAddContactModal] = useState<boolean>(false);
  const [openDeleteContactModal, setOpenDeleteContactModal] = useState<boolean>(false);

  const { mutate: updateContact, isLoading: isUpdating } = useMutation(
    ["contact.update"],
    ({ id, contact }: { id: number; contact: Contact }) => ContactService.update(id, contact),
    {
      onSuccess: () => {
        NotificationToast.success(t("operationPerformedSuccessfully"));
        refetchCompanyContacts();
        setOpenAddContactModal(false);
        setOpenDeleteContactModal(false);
      },
    }
  );

  const handleDeleteContact = (contact: Contact) => {
    if (contact && contact.id) {
      updateContact({
        id: contact.id,
        contact: {
          ...contact,
          valid: contact.userId ? contact.valid : false,
          user:
            contact.userId && contact.user ? { ...contact.user, status: "Inactive" } : undefined,
        },
      });
    }
  };

  const handleConfirmDeleteContact = (contact: Contact) => {
    setContact(keysToCamel(contact));
    setOpenDeleteContactModal(true);
  };

  const handleEditContact = (contact: Contact) => {
    setContact(keysToCamel(contact));
    setOpenAddContactModal(true);
  };

  return (
    <>
      <Modal
        open={openDeleteContactModal}
        onClose={() => setOpenDeleteContactModal(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        style={{ overflowY: "scroll" }}
      >
        <ConfirmDeleteModal
          handleClose={() => setOpenDeleteContactModal(false)}
          handleDeleteContact={(contact) => handleDeleteContact(contact)}
          isLoadingDelete={isUpdating}
          contact={contact}
        />
      </Modal>
      {openAddContactModal && (
        <EditContactModal
          open={openAddContactModal}
          handleClose={() => setOpenAddContactModal(false)}
          handleUpdateOrCreateContact={(contact) => {
            if (contact.id) updateContact({ id: contact.id, contact });
          }}
          isLoading={isUpdating}
          selectedContact={contact}
        />
      )}
      <div className={classes.paper}>
        <div className={classes.modalBody}>
          <header className={classes.modalHeader}>
            <div>
              <div className="d-flex align-items-center mb-2">
                <p className={classes.modalHeaderTitle}>{t("allContacts")}</p>
                <span data-cy="businessContactsCounter" className={classes.count}>
                  {contacts && contacts.length}
                </span>
              </div>
              <span className={classes.modalHeaderSubtitle}>
                {t("seeAllOfYourBusinessContacts")}
              </span>
            </div>
            <IconButton onClick={handleCloseModal}>
              <Close />
            </IconButton>
          </header>
          <section data-cy="businessContactsListContainer" className={classes.modalSection}>
            <ContactsList
              contacts={contacts ? keysToCamel(contacts) : []}
              onSelectDeleteContact={(contact) => handleConfirmDeleteContact(contact)}
              onSelectEditContact={(contact) => handleEditContact(contact)}
              permission={permission}
            />
          </section>
          <section className={classes.modalFooter}>
            <Grid container direction="row" justifyContent="flex-end">
              <Button
                customColor={Color.SECONDARY}
                data-cy="businessContactsCloseButton"
                onClick={handleCloseModal}
              >
                {t("close")}
              </Button>
            </Grid>
          </section>
        </div>
      </div>
    </>
  );
};

export default ContactsListModal;
